import { DateTime } from "luxon";
import React from "react";
import { cn } from "@/lib/utils";

export interface SalonOperatingHours {
  id: string;
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  startTime: string; // "09:00"
  endTime: string; // "17:00"
  slotOrder: number;
}

export interface OperatingHoursOverlayProps {
  /** Operating hours for the day being displayed */
  operatingHours: SalonOperatingHours[];
  /** Start time of the calendar view (in DateTime) */
  startTime: DateTime;
  /** End time of the calendar view (in DateTime) */
  endTime: DateTime;
  /** Height per hour in pixels */
  hourHeight: number;
  /** Additional CSS classes */
  className?: string;
  /** Show operating hours labels */
  showLabels?: boolean;
  /** Opacity of the closed areas (0-1) */
  closedOpacity?: number;
}

/**
 * OperatingHoursOverlay - Reusable component to visualize salon operating hours
 *
 * Features:
 * - Shows grayed-out areas for closed hours
 * - Highlights open business hours
 * - Supports multiple time slots per day (lunch breaks, etc.)
 * - Responsive labels and positioning
 * - Used in both waitlist calendar and main calendar views
 */
export function OperatingHoursOverlay({
  operatingHours,
  startTime,
  endTime,
  hourHeight,
  className,
  showLabels = true,
  closedOpacity = 0.3,
}: OperatingHoursOverlayProps) {
  // Generate visual blocks for closed and open periods
  const timeBlocks = React.useMemo(() => {
    if (!operatingHours.length) {
      // If no operating hours, show entire period as closed
      return [
        {
          type: "closed" as const,
          start: startTime,
          end: endTime,
          label: "Closed",
        },
      ];
    }

    const blocks: Array<{
      type: "open" | "closed";
      start: DateTime;
      end: DateTime;
      label: string;
    }> = [];

    // Sort operating hours by start time
    const sortedHours = [...operatingHours]
      .map((hour) => ({
        ...hour,
        startDateTime: startTime.startOf("day").set({
          hour: Number.parseInt(hour.startTime.split(":")[0], 10),
          minute: Number.parseInt(hour.startTime.split(":")[1], 10),
        }),
        endDateTime: startTime.startOf("day").set({
          hour: Number.parseInt(hour.endTime.split(":")[0], 10),
          minute: Number.parseInt(hour.endTime.split(":")[1], 10),
        }),
      }))
      .sort((a, b) => a.startDateTime.toMillis() - b.startDateTime.toMillis());

    let currentTime = startTime;

    for (const hours of sortedHours) {
      // Add closed period before this operating period
      if (currentTime < hours.startDateTime) {
        blocks.push({
          type: "closed",
          start: currentTime,
          end: hours.startDateTime,
          label: "Closed",
        });
      }

      // Add open period
      const openStart = currentTime > hours.startDateTime ? currentTime : hours.startDateTime;
      const openEnd = endTime < hours.endDateTime ? endTime : hours.endDateTime;

      if (openStart < openEnd) {
        blocks.push({
          type: "open",
          start: openStart,
          end: openEnd,
          label: `Open ${hours.startTime} - ${hours.endTime}`,
        });
      }

      currentTime = hours.endDateTime;
    }

    // Add final closed period if needed
    if (currentTime < endTime) {
      blocks.push({
        type: "closed",
        start: currentTime,
        end: endTime,
        label: "Closed",
      });
    }

    return blocks.filter((block) => block.start < block.end);
  }, [operatingHours, startTime, endTime]);

  return (
    <div className={cn("absolute inset-0 pointer-events-none", className)}>
      {timeBlocks.map((block, _index) => {
        const minutesFromStart = block.start.diff(startTime, "minutes").minutes;
        const blockDurationMinutes = block.end.diff(block.start, "minutes").minutes;

        const topPixels = (minutesFromStart / 60) * hourHeight;
        const heightPixels = (blockDurationMinutes / 60) * hourHeight;

        if (block.type === "closed") {
          return (
            <div
              key={`closed-${block.start.toISO()}`}
              className="absolute left-0 right-0 bg-gray-500/20 dark:bg-gray-700/30 border-l-4 border-red-400/60"
              style={{
                top: `${topPixels}px`,
                height: `${heightPixels}px`,
                opacity: closedOpacity,
              }}
            >
              {showLabels && heightPixels > 30 && (
                <div className="flex items-center h-full">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium ml-6 bg-white/80 dark:bg-gray-900/80 px-2 py-1 rounded">
                    CLOSED
                  </div>
                </div>
              )}
            </div>
          );
        }

        return (
          <div
            key={`open-${block.start.toISO()}`}
            className="absolute left-0 right-0 border-l-4 border-green-400/60"
            style={{
              top: `${topPixels}px`,
              height: `${heightPixels}px`,
            }}
          >
            {showLabels && heightPixels > 40 && (
              <div className="flex items-start pt-2 h-full">
                <div className="text-xs text-green-700 dark:text-green-400 font-medium ml-6 bg-green-50/90 dark:bg-green-900/80 px-2 py-1 rounded">
                  OPEN {block.start.toFormat("h:mm a")} - {block.end.toFormat("h:mm a")}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Hook to get operating hours for a specific date
 */
export function useOperatingHoursForDate(
  operatingHours: SalonOperatingHours[],
  date: DateTime
): SalonOperatingHours[] {
  return React.useMemo(() => {
    const dayOfWeek = date.weekday % 7; // Convert Luxon weekday (1-7) to (0-6)
    return operatingHours.filter((hour) => hour.dayOfWeek === dayOfWeek);
  }, [operatingHours, date]);
}
